import { Button, CircularProgress, Grid, LinearProgress, Typography } from '@material-ui/core';
import { TOOLBAR_HEIGHT } from 'components/Layout';
import { Form, Formik } from 'formik';
import type { ChangeEvent } from 'react';
import React, { useRef } from 'react';

import useStyle from './styles';

import type { FormikProps } from 'formik';

export interface IError {
  helperText: string;
  error: boolean;
}

export interface IForm<T = any> {
  handleChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  getError: (field: string) => IError;
  setFocus?: (event: any) => void;
  setFieldValue: (fieldName: string, value: any) => void;
  formik?: FormikProps<T>;
  values: T;
  initialValues: T;
  setSubmitting: (status: boolean) => void;
}

interface IChildren {
  id: string;
  title: string;
  anchor: string;
  content: any;
  validations?: any;
  submit?: any;
  defaultValues?: any;
  isLoading?: boolean;
}

interface IProps {
  classes?: any;
  children: Array<IChildren>;
}

function Content(props: IProps) {
  const { children } = props;
  const classes = useStyle(props);
  const sectionsRef = useRef<HTMLElement[]>([]);

  function handleAnchorClick(id: string) {
    if (sectionsRef.current[id]) {
      sectionsRef.current[id].scrollIntoView();
    }
  }

  return (
    <Grid component='aside' container className={classes.root} justify='space-between' spacing={2}>
      <Grid item md={3} xs={12}>
        <div className={classes.anchorContent} style={{ top: TOOLBAR_HEIGHT }}>
          {children.map((c: IChildren) => (
            <Typography key={`anchor-${c.id}`} className={classes.tAnchor}>
              <button className={classes.anchor} onClick={() => handleAnchorClick(c.id)}>
                {c.anchor}
              </button>
            </Typography>
          ))}
        </div>
      </Grid>
      <Grid item md={6} xs={12}>
        {children.map((form: IChildren) => {
          const handleSubmit = async (values: any, { setSubmitting, setValues, resetForm }: FormikProps<any>) => {
            await form.submit(values);
            setValues(values);
            setSubmitting(false);
            resetForm();
          };

          return (
            <Formik
              key={`content-${form.id}`}
              initialValues={form.defaultValues}
              validationSchema={form.validations}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({
                isSubmitting,
                errors,
                values,
                handleChange,
                handleBlur,
                isValid,
                dirty,
                setFieldValue,
                setSubmitting,
                initialValues,
              }: FormikProps<IForm>) => {
                const getError = (field: string): IError => {
                  const error = errors[field];
                  if (!error) {
                    return { error: false, helperText: null };
                  }
                  return {
                    helperText: String(error),
                    error: true,
                  };
                };
                return (
                  <Form>
                    <section className={classes.content} ref={(ref) => (sectionsRef.current[form.id] = ref)}>
                      {!!form.title && (
                        <Grid className={classes.titleContent} container justify='space-between' alignItems='center' style={{ top: TOOLBAR_HEIGHT }}>
                          <Grid item>
                            <Typography className={classes.title}>{form.title}</Typography>
                          </Grid>
                          <Grid item>
                            <div className={classes.buttonProgressWrapper}>
                              {form.submit && (
                                <Button
                                  type='submit'
                                  disabled={!isValid || !dirty || form.isLoading || isSubmitting}
                                  variant='contained'
                                  color='secondary'
                                >
                                  Salvar
                                </Button>
                              )}
                              {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                            </div>
                          </Grid>
                          {isSubmitting && <LinearProgress className={classes.loadingLinear} />}
                        </Grid>
                      )}
                      <div className={isSubmitting ? classes.formSubmiting : classes.form} style={{ padding: !form.title && 0 }}>
                        {form.content({
                          errors,
                          values,
                          handleChange,
                          handleBlur,
                          getError,
                          isSubmitting,
                          setFieldValue,
                          setSubmitting,
                          initialValues,
                        })}
                      </div>
                    </section>
                  </Form>
                );
              }}
            </Formik>
          );
        })}
      </Grid>
      <Grid item md={3} />
    </Grid>
  );
}

export default Content;
